import { Box, Button } from "@mui/material";
import { SubmissionStepStatus, SubmissionStepType } from "generated-graphql/graphql";
import { useStepperNavigation } from "hooks/useStepperNavigation";
import { SubmissionStepState } from "hooks/useSubmissionSteps";
import { useTouchSubmissionMutation } from "hooks/useTouchSubmissionMutation";
import { useWasteStepper } from "hooks/useWasteStepper";
import { useCallback } from "react";

/**
 * Shared footer: records the step complete and navigates onward. Hidden while
 * frozen or (unless `canContinue` overrides) when the user cannot edit.
 */
export function StepContinueFooter({
  submissionId,
  step,
  backStep,
  label = "Save",
  canContinue,
  onContinue,
  nextStep,
  loading: loadingOverride,
}: {
  submissionId: string;
  step: SubmissionStepType;
  /** Adds a "Back" button that navigates without touching step status. */
  backStep?: SubmissionStepType;
  label?: string;
  /** Overrides the edit gate for steps with their own completion rules. */
  canContinue?: boolean;
  /** Replaces the shared-step touch (e.g. a reviewer stamping their own row). */
  onContinue?: () => Promise<void> | void;
  /** Continue navigates here instead of the next step in order. */
  nextStep?: SubmissionStepType;
  loading?: boolean;
}) {
  const { isFrozen, canEdit, stepStates } = useWasteStepper();
  const { touchStep, loading: touchLoading } = useTouchSubmissionMutation(submissionId);
  const { goToStep, goToNextStep } = useStepperNavigation();

  const loading = loadingOverride ?? touchLoading;
  // Revisiting a finished step: "Continue"/"Save" reads like there's still
  // work to do here, so acknowledge it's already done and just move on.
  const displayLabel = stepStates[step] === SubmissionStepState.Done ? "Next" : label;

  const handleContinue = useCallback(async () => {
    if (onContinue) {
      await onContinue();
    } else {
      await touchStep(step, SubmissionStepStatus.Completed);
    }
    if (nextStep) {
      goToStep(nextStep);
    } else {
      goToNextStep(step);
    }
  }, [onContinue, touchStep, step, nextStep, goToStep, goToNextStep]);

  if (isFrozen || !(canContinue ?? canEdit)) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
      {backStep && (
        <Button variant="text" disabled={loading} onClick={() => goToStep(backStep)}>
          Back
        </Button>
      )}
      <Button variant="contained" loading={loading} onClick={handleContinue}>
        {displayLabel}
      </Button>
    </Box>
  );
}
